/**
 * TestPressSupplierHistory
 *
 * Compact header + collapsed-by-default history block that sits
 * above the Supplier → Test Press workflow (``SupplierConfirmationSection
 * variant="test_press"``). It makes the ACTIVE test press version
 * visibly explicit — e.g. "Test Press · V2" — so admins can tell at
 * a glance which cycle the PO / Order Confirmation / Dispatch flow
 * below belongs to.
 *
 * When previous cycles exist (V1 and up), it also offers a
 * secondary "Previous test presses (N)" disclosure. Each prior
 * cycle expands read-only to its sealed Purchase Order,
 * Confirmation, and Dispatch snapshot — pulled from the same
 * ``test_press_versions`` collection used by the TestPressCard
 * history (no second history system).
 *
 * Nothing in here mutates data. The active workflow continues to
 * live in ``SupplierConfirmationSection`` which only ever reads
 * the ACTIVE ``supplier_confirmations`` row for the TP variant.
 */
import { useState, useEffect } from 'react';
import { Disc3, ChevronRight, ChevronDown, History, FileText, ClipboardCheck, Truck, Loader2 } from 'lucide-react';
import { ordersApi } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';

function CycleRow({ v }) {
    const [open, setOpen] = useState(false);
    const sc = v.supplier_confirmation || {};
    const tp = v.test_press_fields || {};
    // Expected arrival may live on the order's flat field (snapshot
    // ``test_press_fields``) OR on the sealed supplier confirmation
    // row's dispatch_planned_arrival_date. We prefer the dispatch
    // field for the admin surface because it reflects what Sony
    // promised at the time the cycle closed.
    const dispatchArrival = sc.dispatch_planned_arrival_date || tp.test_press_expected_arrival;

    return (
        <div
            className="border border-slate-200 rounded-md overflow-hidden"
            data-testid={`tp-sup-history-v${v.version}`}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                data-testid={`tp-sup-history-v${v.version}-toggle`}
            >
                {open
                    ? <ChevronDown className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                    : <ChevronRight className="h-3.5 w-3.5 text-slate-500 shrink-0" />}
                <span className="text-sm font-semibold text-slate-800">V{v.version}</span>
                <span className="text-xs text-slate-500">
                    {v.closed_reason === 'new_master'
                        ? 'Closed · New master required'
                        : v.closed_reason === 'new_tp_v2'
                            ? 'Closed · New Test Press'
                            : `Closed · ${v.closed_reason || 'cycle ended'}`}
                </span>
                {v.closed_at && (
                    <span className="text-[10px] text-slate-400 ml-auto">
                        {formatDate(v.closed_at)}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3"
                    data-testid={`tp-sup-history-v${v.version}-body`}
                >
                    {/* Purchase Order snapshot — read-only */}
                    <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                            <FileText className="h-3 w-3" /> Purchase Order
                        </p>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                            {sc.internal_po_number && (
                                <>
                                    <dt className="text-slate-400">Internal PO</dt>
                                    <dd className="font-mono">{sc.internal_po_number}</dd>
                                </>
                            )}
                            {sc.sony_leos_number && (
                                <>
                                    <dt className="text-slate-400">LEOS</dt>
                                    <dd className="font-mono">{sc.sony_leos_number}</dd>
                                </>
                            )}
                            {sc.po_sent_at && (
                                <>
                                    <dt className="text-slate-400">Sent to Sony</dt>
                                    <dd>{formatDateTime(sc.po_sent_at)}</dd>
                                </>
                            )}
                            {sc.po_sent_by_name && (
                                <>
                                    <dt className="text-slate-400">Sent by</dt>
                                    <dd>{sc.po_sent_by_name}</dd>
                                </>
                            )}
                        </dl>
                        {!sc.po_sent_at && !sc.sony_leos_number && (
                            <p className="text-[11px] text-slate-400 italic">PO data not recorded for this cycle.</p>
                        )}
                    </div>

                    {/* Order Confirmation snapshot — read-only */}
                    <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                            <ClipboardCheck className="h-3 w-3" /> Order Confirmation
                        </p>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                            {sc.status && (
                                <>
                                    <dt className="text-slate-400">Status</dt>
                                    <dd>{sc.status}</dd>
                                </>
                            )}
                            {sc.received_at && (
                                <>
                                    <dt className="text-slate-400">Received</dt>
                                    <dd>{formatDateTime(sc.received_at)}</dd>
                                </>
                            )}
                            {sc.approved_at && (
                                <>
                                    <dt className="text-slate-400">Approved</dt>
                                    <dd>{formatDateTime(sc.approved_at)}</dd>
                                </>
                            )}
                            {sc.approved_by_name && (
                                <>
                                    <dt className="text-slate-400">Approved by</dt>
                                    <dd>{sc.approved_by_name}</dd>
                                </>
                            )}
                        </dl>
                    </div>

                    {/* Dispatch snapshot — read-only */}
                    <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                            <Truck className="h-3 w-3" /> Dispatch
                        </p>
                        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
                            {sc.delivery_note_number && (
                                <>
                                    <dt className="text-slate-400">Delivery note</dt>
                                    <dd className="font-mono">{sc.delivery_note_number}</dd>
                                </>
                            )}
                            {sc.pallet_number && (
                                <>
                                    <dt className="text-slate-400">Pallet</dt>
                                    <dd className="font-mono">{sc.pallet_number}</dd>
                                </>
                            )}
                            {sc.shipped_quantity != null && (
                                <>
                                    <dt className="text-slate-400">Shipped qty</dt>
                                    <dd>{sc.shipped_quantity}</dd>
                                </>
                            )}
                            {dispatchArrival && (
                                <>
                                    <dt className="text-slate-400">Planned arrival</dt>
                                    <dd>{formatDate(dispatchArrival)}</dd>
                                </>
                            )}
                            {sc.dispatch_approved_at && (
                                <>
                                    <dt className="text-slate-400">Dispatched</dt>
                                    <dd>{formatDateTime(sc.dispatch_approved_at)}</dd>
                                </>
                            )}
                        </dl>
                        {!sc.delivery_note_number && !sc.dispatch_approved_at && (
                            <p className="text-[11px] text-slate-400 italic">Dispatch not recorded for this cycle.</p>
                        )}
                    </div>

                    {/* Why this cycle closed (admin-only context) */}
                    {v.sony_response_note && (
                        <p className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                            <span className="text-amber-700 font-semibold">Sony response note:</span>{' '}
                            {v.sony_response_note}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export function TestPressSupplierHistory({ order }) {
    const tpVersion = Number(order?.test_press_version || 1);
    const [open, setOpen] = useState(false);
    const [versions, setVersions] = useState(null);
    const [loading, setLoading] = useState(false);

    // Lazy load on first expand; refetches whenever a new cycle is
    // sealed (``test_press_updated_at`` bumps on the live order).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        ordersApi.getTestPressHistory(order.id)
            .then((r) => { if (!cancelled) setVersions(r.data?.versions || []); })
            .catch(() => { if (!cancelled) setVersions([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, order?.id, order?.test_press_updated_at]);

    const hasPrevious = tpVersion > 1;

    return (
        <div className="mb-3" data-testid="tp-supplier-version-banner">
            {/* Active version chip — always on, prominently coloured
                so the admin can confirm "V2 / V3 / etc." before
                touching the PO / Confirmation / Dispatch below. */}
            <div className="flex items-center justify-between gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-1.5">
                <div className="flex items-center gap-2 text-sm">
                    <Disc3 className="h-4 w-4 text-indigo-600 shrink-0" />
                    <span className="text-slate-800 font-semibold">
                        Test Press · <span data-testid="tp-active-version-label">V{tpVersion}</span>
                    </span>
                    {hasPrevious && (
                        <span className="text-[11px] text-slate-500">
                            · active supplier workflow
                        </span>
                    )}
                </div>
                {hasPrevious && (
                    <button
                        type="button"
                        onClick={() => setOpen((o) => !o)}
                        className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
                        data-testid="tp-supplier-history-toggle"
                    >
                        <History className="h-3 w-3" />
                        Previous test presses ({Math.max(0, tpVersion - 1)})
                        {open
                            ? <ChevronDown className="h-3 w-3" />
                            : <ChevronRight className="h-3 w-3" />}
                    </button>
                )}
            </div>
            {hasPrevious && open && (
                <div className="mt-2 space-y-2" data-testid="tp-supplier-history-list">
                    {loading && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading supplier history…
                        </p>
                    )}
                    {!loading && versions && versions.length === 0 && (
                        <p className="text-xs text-slate-500">No previous test press cycles.</p>
                    )}
                    {!loading && versions && versions.map((v) => <CycleRow key={v.version} v={v} />)}
                </div>
            )}
        </div>
    );
}

export default TestPressSupplierHistory;
